import { AlertCircle, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { QtyStepper } from './QtyStepper';
import { MoneyValue } from './MoneyValue';
import { lineSubtotal, seatUnitPrice, type CartLine as CartLineType } from '@/stores/cart.store';

interface CartLineProps {
  line: CartLineType;
  onQty: (key: string, qty: number) => void;
  onRemove: (key: string) => void;
  focused?: boolean;
}

export function CartLine({ line, onQty, onRemove, focused }: CartLineProps) {
  const qty = line.seats.length;
  const addOnCount = line.seats.reduce((s, seat) => s + seat.addOns.length, 0);
  const needsSlots = line.requiresSlot && line.seats.some((seat) => !seat.slotId);
  // Seats in differently priced slots (a time-of-day rate) show a range.
  const seatPrices = line.seats.map((seat) => seatUnitPrice(line, seat));
  const minPrice = seatPrices.length ? Math.min(...seatPrices) : line.unitPrice;
  const maxPrice = seatPrices.length ? Math.max(...seatPrices) : line.unitPrice;

  return (
    <div
      className="animate-cart-in rounded-lg border-2 bg-card p-3 transition-colors"
      style={{ borderColor: focused ? 'hsl(var(--ring))' : undefined }}
      data-focused={focused || undefined}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-semibold text-ink-900">{line.name}</p>
          <span className="mt-0.5 inline-flex items-center gap-1 text-xs text-ink-600">
            <MoneyValue value={minPrice} className="text-xs" />
            {maxPrice !== minPrice && (
              <>
                – <MoneyValue value={maxPrice} className="text-xs" />
              </>
            )}{' '}
            / tiket
          </span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 text-ink-400 hover:text-destructive"
          onClick={() => onRemove(line.key)}
          aria-label={`Hapus ${line.name}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      {addOnCount > 0 && (
        <p className="mt-1.5 text-xs text-ink-400">{addOnCount} tambahan dipilih</p>
      )}
      {needsSlots && (
        <p className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-warning">
          <AlertCircle className="h-3.5 w-3.5" /> Perlu pilih waktu
        </p>
      )}

      <div className="mt-3 flex items-center justify-between gap-2">
        <QtyStepper value={qty} min={1} onChange={(v) => onQty(line.key, v)} />
        <MoneyValue value={lineSubtotal(line)} emphasis className="text-lg" />
      </div>
    </div>
  );
}
